import cn from "classnames";
import { API_STATUS } from "core-lib/constants/network";
import { useProfile } from "core-lib/hooks/useProfile";
import { trackCustomGTMEvent } from "core-lib/utils/events";
import { useRouter } from "next/router";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import BookingForm from "testDrive/components/popups/BookingForm";
import ServiceableOemPopup from "testDrive/components/popups/ServiceableOemPopup";
import { LeadGenerationSource, LeadType } from "testDrive/constants";
import { useCheckServiceable } from "testDrive/hooks/useCheckServiceable";
import { useUpdateBooking } from "testDrive/hooks/useUpdateBooking";
import { fetchGenerateLead } from "testDrive/services/fetchGenerateLead";
import { fetchList } from "testDrive/services/fetchList";
import {
  updateLeadVerification,
  updateServiceableOemPopup,
  updateServiceablePopup,
  updateWebSuccess,
} from "testDrive/store";
import { UserDetailsType } from "testDrive/types";
import { checkIfAllUserDetailsExist, getName } from "testDrive/utils";
import {
  Attributes,
  PageNames,
  TestDriveEvents,
  trackTestDriveEvents,
} from "testDrive/utils/events";
import { AnimatedImage, Button, Checkbox, Typography } from "ui";
import s from "./SimilarRangeCars.module.scss";

interface SimilarRangeCarsProps {
  payload: any;
  address: any;
  pageName: PageNames;
  userDetails: UserDetailsType;
  title: string;
  bgLess?: boolean;
  defaultSingleCheck?: boolean;
  showText?: boolean;
  dynamicText?: boolean;
  leadGenerationSource?: LeadGenerationSource;
}

const SimilarRangeCars = ({
  pageName,
  payload,
  address,
  userDetails,
  title = "",
  bgLess = false,
  defaultSingleCheck = false,
  showText = false,
  dynamicText = false,
  leadGenerationSource = null,
}: SimilarRangeCarsProps) => {
  const [data, setData] = useState(null);
  const [apiStatus, setApiStatus] = useState(API_STATUS.default);
  const [isChecked, setIsChecked] = useState({});
  const [showFormPopup, setShowFormPopup] = useState(false);
  const { phoneNumber } = useProfile();
  const [generatingLead, setGeneratingLead] = useState(false);
  const { updateStateData } = useUpdateBooking();
  const { checkServiceable } = useCheckServiceable();
  const [finalList, setFinalList] = useState(null);

  const router = useRouter();

  const noRedirect = useSelector((state: any) => state?.testDrive?.noRedirect);

  const formDetails = useSelector(
    (state: any) => state?.testDrive?.formDetails
  );
  const showOemServiceablePopup = useSelector(
    (state: any) => state?.testDrive?.showOemServiceablePopup
  );

  const dispatch = useDispatch();

  const count = useMemo(
    () =>
      Object.keys(isChecked || {}).filter((item) => !!isChecked[item]).length,
    [isChecked]
  );

  const nonBookedCars = useMemo(
    () => data?.list?.filter((item) => !item?.isBooked) || [],
    [data]
  );

  const oemCars = useMemo(
    () => nonBookedCars?.filter((item) => item?.leadType === LeadType.Oem),
    [nonBookedCars]
  );

  const checkedList = useMemo(
    () => nonBookedCars?.filter((item) => !!isChecked[item?.id]),
    [isChecked, nonBookedCars]
  );

  const getData = async () => {
    setApiStatus(API_STATUS.in_progress);
    const response = await fetchList(payload, false);
    if (response) {
      setData(response);
      setApiStatus(API_STATUS.success);
    } else {
      setApiStatus(API_STATUS.failure);
    }
  };

  const handleChange = (item) => {
    setIsChecked((prev) => ({
      ...prev,
      [item?.id]: !prev?.[item?.id],
    }));
    if (!!isChecked[item?.id]) {
      trackTestDriveEvents(TestDriveEvents.ModelUnselected, {
        [Attributes.PageName]: pageName,
        [Attributes.ModelId]: item?.displayModelName,
      });
    } else {
      trackTestDriveEvents(TestDriveEvents.ModelSelected, {
        [Attributes.PageName]: pageName,
        [Attributes.ModelId]: item?.displayModelName,
      });
    }
  };

  const createLead = async ({ list, location }) => {
    let isServiceable = true;
    const dealerCarIdx = list?.findIndex(
      (item) => item?.leadType === LeadType.Dealer
    );
    if (dealerCarIdx > -1) {
      isServiceable = checkServiceable({
        leadType: LeadType.Dealer,
        city: location?.data?.city,
      });
    }
    if (!isServiceable) {
      dispatch(updateServiceablePopup(true));
      return;
    }
    setGeneratingLead(true);
    const detailsOfUser = data?.userDetails || userDetails;
    const { firstName, lastName } = getName(
      detailsOfUser?.name || formDetails?.name?.input || ""
    );

    const ids = [];
    if (list?.length > 0) {
      list?.forEach((item) => ids.push(item?.id));
    }

    const payload: any = {
      first_name: firstName,
      last_name: lastName || firstName,
      email: detailsOfUser?.email,
      city: location?.data?.city,
      inventory_ids: ids,
      meta_data: {
        latitude: location?.data?.latitude,
        longitude: location?.data?.longitude,
      },
      state: location?.data?.state,
      infra_city: location?.data?.infraCity,
      zone_name: location?.data?.zoneName,
      state_id: location?.data?.stateId,
    };
    if (location?.data?.pincode) {
      payload.pincode = `${location?.data.pincode}`;
    }

    if (location?.data?.city) {
      payload.city = location?.data?.city;
    }
    if (leadGenerationSource) {
      payload.generated_source = leadGenerationSource;
    }
    const response = await fetchGenerateLead(payload);
    if (response) {
      if (!noRedirect) {
        let url = `/new-cars-v2/confirmation`;
        const query: any = {
          ids: response,
        };
        dispatch(updateLeadVerification({ show: true, leadIds: response }));
      } else {
        dispatch(updateWebSuccess(true));
      }
      updateStateData({ ids: ids, leadIds: response, isBooked: true });
      dispatch(updateServiceablePopup(false));
      dispatch(updateServiceableOemPopup(false));
      setApiStatus(API_STATUS.success);
      trackTestDriveEvents(TestDriveEvents.LeadSubmitted, {
        [Attributes.PageName]: pageName,
        [Attributes.ModelId]: checkedList
          ?.map((item) => item?.displayModelName)
          .join(","),
        [Attributes.Section]: "Similarly priced cars",
        [Attributes.Leads] : response

      });
      response?.map((item) => {
        trackCustomGTMEvent({
          eventName: "leadCaptured",
          attributes: { eventID: item },
        });
      });
    }
    setGeneratingLead(false);
  };

  const handleClick = (list, city) => {
    let isServiceable = true;
    const dealerCarIdx = list?.findIndex(
      (item) => item?.leadType === LeadType.Dealer
    );

    if (dealerCarIdx > -1) {
      isServiceable = checkServiceable({
        leadType: LeadType.Dealer,
        city: city?.city,
      });
    }
    const allDetailsAvailable = checkIfAllUserDetailsExist({
      list: checkedList,
      userDetails: userDetails,
      address: { data: city },
      formDetails: city?.city ? formDetails : null,
    });

    if (!isServiceable) {
      if (oemCars?.length > 0) {
        dispatch(updateServiceableOemPopup(true));
      } else {
        dispatch(updateServiceablePopup(true));
      }
    } else if (allDetailsAvailable) {
      createLead({ list, location: { data: city } });
    } else {
      setFinalList(list);
      setShowFormPopup(true);
      trackTestDriveEvents(TestDriveEvents.BookTestDriveClicked, {
        [Attributes.PageName]: pageName,
        [Attributes.ModelId]: list
          ?.map((item) => item?.displayModelName)
          ?.join(", "),
        [Attributes.Section]: "Similarly priced cars",
      });
    }
  };

  const getText = useMemo(() => {
    if (!showText) return null;
    const model = checkedList?.find(
      (item) => !!item?.metaData?.fetchDealersList
    );
    if (model) {
      return `Provide details for ${model?.displayModelName}`;
    }
    return null;
  }, [checkedList, showText]);

  useEffect(() => {
    if (data?.list?.length) {
      const obj = {};
      data?.list.forEach((item) => {
        if (defaultSingleCheck) {
          if (Object.keys(obj).length === 0) {
            if (!item?.isBooked) {
              obj[item?.id] = true;
            }
          }
        } else if (!item?.isBooked) {
          obj[item?.id] = true;
        }
      });
      setIsChecked(obj);
    }
  }, [data]);

  useEffect(() => {
    getData();
  }, []);

  if (
    ![API_STATUS.in_progress, API_STATUS.default].includes(apiStatus) &&
    (data?.list?.length === 0 || !data?.list)
  ) {
    return null;
  }

  if ([API_STATUS.default, API_STATUS.in_progress].includes(apiStatus)) {
    return (
      <div
        className={cn(s.shimmer, "cmn_shimmer", { [s.bglessShimmer]: bgLess })}
      ></div>
    );
  }

  return (
    <div className={cn(s.root, { [s.bgLess]: bgLess })}>
      <div className={s.main}>
        {title && <Typography label={title} variant="h3" />}
        <div className={cn(s.list, "cmn_hide_scrollbar")}>
          {data?.list?.map((item, idx) => (
            <div
              key={`${idx}%^&*`}
              className={s.card}
              onClick={(e) => {
                e.stopPropagation();
                if (!item?.isBooked) {
                  handleChange(item);
                }
              }}
            >
              <AnimatedImage
                src={item?.metaData?.bannerImage}
                className={s.image}
              />
              <div className={s.textStack}>
                <Typography
                  label={item?.displayModelName}
                  variant="small_r"
                  color="text_subheading"
                  style={{ marginBottom: "4px" }}
                />
                <Typography label={item?.displayPrice} variant="body_para_m" />
                <Typography
                  label="onwards"
                  variant="small_r"
                  color="text_subheading"
                />
              </div>
              {!item?.isBooked && (
                <div className={s.checkBox}>
                  <Checkbox
                    isChecked={isChecked?.[item?.id] === true}
                    onChange={(e) => {
                      e.stopPropagation();
                      handleChange(item);
                    }}
                  />
                </div>
              )}
              {item?.isBooked && (
                <div className={s.overlay}>
                  <Typography
                    label="Booked"
                    variant="small_r"
                    color="disabled_background"
                    className={s.overlayText}
                  />
                </div>
              )}
            </div>
          ))}
        </div>
        <Button
          label={
            dynamicText && checkedList?.length > 1
              ? `Book test drive for ${checkedList?.length} cars`
              : "Book now"
          }
          isLoading={generatingLead}
          isDisabled={count === 0 || generatingLead}
          fullwidth
          onClick={() => {
            handleClick(checkedList, address?.data);
          }}
        />
      </div>

      <ServiceableOemPopup
        show={showOemServiceablePopup}
        onClose={() => {
          dispatch(updateServiceableOemPopup(false));
        }}
        list={oemCars}
        handleClick={(list) => {
          handleClick(list, address?.data);
        }}
        isLoading={generatingLead}
        totalCars={nonBookedCars?.length - oemCars?.length}
      />

      <BookingForm
        show={showFormPopup}
        onClose={() => {
          setShowFormPopup(false);
        }}
        userDetails={data?.userDetails || userDetails}
        address={address}
        pageName={pageName}
        list={finalList}
        section="Similarly priced cars"
        showServiceablePopup={false}
        cityChange={(city) => {
          handleClick(checkedList, city);
        }}
        modelText={getText}
      />
    </div>
  );
};

export default SimilarRangeCars;
